import {
  createContext,
  useCallback,
  useContext,
  useEffect,
  useState,
  type ReactNode,
} from 'react';
import { getPreferredCurrency, type PreferredCurrency } from '../lib/maison-adji-api';

type ContextValue = {
  preferredCurrency: PreferredCurrency;
  loading: boolean;
  refreshPreferredCurrency: () => Promise<void>;
};

const Context = createContext<ContextValue | null>(null);

export function CurrencySettingsProvider({ children }: { children: ReactNode }) {
  const [preferredCurrency, setPreferredCurrency] = useState<PreferredCurrency>('XOF');
  const [loading, setLoading] = useState(true);

  const refreshPreferredCurrency = useCallback(async () => {
    const currency = await getPreferredCurrency().catch(() => 'XOF' as const);
    setPreferredCurrency(currency);
  }, []);

  useEffect(() => {
    getPreferredCurrency()
      .then(setPreferredCurrency)
      .catch(() => setPreferredCurrency('XOF'))
      .finally(() => setLoading(false));
  }, []);

  return (
    <Context.Provider value={{ preferredCurrency, loading, refreshPreferredCurrency }}>
      {children}
    </Context.Provider>
  );
}

export function usePreferredCurrency() {
  const ctx = useContext(Context);
  if (!ctx) throw new Error('usePreferredCurrency must be used within CurrencySettingsProvider');
  return ctx;
}

export function usePreferredCurrencyOptional(): ContextValue | null {
  return useContext(Context);
}
